import React from 'react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import DateSelect from '../ui/DateSelect';

const ranges = [
  { id: '8weeks', label: 'Last 8 weeks' },
  { id: 'term', label: 'Term' },
  { id: 'custom', label: 'Custom' }
];

export default function AttendanceSummaryCard({
  rate,
  threshold,
  delta,
  statusLabel,
  statusVariant,
  timeRange,
  onRangeChange,
  customRange,
  onCustomRangeChange,
  stats,
  recentAbsences,
  isLoading = false,
  error = ''
}) {
  if (isLoading) {
    return <Card className="p-5">Loading attendance summary…</Card>;
  }
  if (error) {
    return <Card className="p-5 sp-alert sp-alert--critical text-sm">{error}</Card>;
  }
  if (rate === null || rate === undefined) {
    return <Card className="p-5 text-sm sp-muted">No attendance data available.</Card>;
  }

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Attendance Summary</h3>
          <p className="text-xs sp-muted">Why this student is flagged right now.</p>
        </div>
        <div className="sp-segment" role="tablist" aria-label="Attendance time range">
          {ranges.map((range) => (
            <button
              key={range.id}
              type="button"
              role="tab"
              aria-selected={timeRange === range.id}
              className={timeRange === range.id ? 'active' : ''}
              onClick={() => onRangeChange(range.id)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {timeRange === 'custom' && (
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="sp-label">From</label>
            <DateSelect
              value={customRange?.start || ''}
              onChange={(value) => onCustomRangeChange({ ...customRange, start: value })}
              className="mt-2 w-full"
            />
          </div>
          <div>
            <label className="sp-label">To</label>
            <DateSelect
              value={customRange?.end || ''}
              onChange={(value) => onCustomRangeChange({ ...customRange, end: value })}
              className="mt-2 w-full"
            />
          </div>
          <p className="text-xs sp-muted md:col-span-2">
            TODO: Apply custom range to attendance calculations when API is available.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-[1.2fr_1fr] gap-4 items-center">
        <div>
          <div className="flex items-end gap-3">
            <span className="sp-value" style={{ fontSize: '32px', fontWeight: 700 }}>{rate}%</span>
            <Badge variant={statusVariant}>{statusLabel}</Badge>
          </div>
          <p className="text-sm sp-muted mt-2">
            Threshold: {threshold}% · {delta >= 0 ? `+${delta}` : delta}% vs threshold
          </p>
          <p className="text-xs sp-muted mt-1">
            Recent absences (14 days): {recentAbsences}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="sp-panel sp-panel--muted p-3">
            <p className="text-xs sp-muted">Sessions attended</p>
            <p className="sp-value" style={{ fontWeight: 600 }}>{stats?.attended || 0}</p>
          </div>
          <div className="sp-panel sp-panel--muted p-3">
            <p className="text-xs sp-muted">Authorised absences</p>
            <p className="sp-value" style={{ fontWeight: 600 }}>{stats?.authorised || 0}</p>
          </div>
          <div className="sp-panel sp-panel--muted p-3">
            <p className="text-xs sp-muted">Unauthorised absences</p>
            <p className="sp-value" style={{ fontWeight: 600 }}>{stats?.unauthorised || 0}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
